import { Injectable } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand
} from '@aws-sdk/client-cognito-identity';
import { environment } from '@env/environment';
import { firstValueFrom, filter, take, timeout, catchError, of } from 'rxjs';

export interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: Date;
}

@Injectable({ providedIn: 'root' })
export class AwsCredentialsService {
  private credentials: AwsCredentials | null = null;
  private refreshPromise: Promise<AwsCredentials> | null = null;

  private readonly cognitoClient = new CognitoIdentityClient({
    region: environment.cognito.region
  });

  constructor(private readonly oidcSecurityService: OidcSecurityService) {}

  async getCredentials(): Promise<AwsCredentials> {
    if (this.credentials && !this.isExpiringSoon()) {
      return this.credentials;
    }

    // Deduplicate concurrent refresh calls
    if (!this.refreshPromise) {
      this.refreshPromise = this.refreshCredentials().finally(() => {
        this.refreshPromise = null;
      });
    }

    return this.refreshPromise;
  }

  private isExpiringSoon(): boolean {
    if (!this.credentials?.expiration) return true;
    // Refresh 60 seconds before expiry
    return this.credentials.expiration.getTime() - Date.now() < 60_000;
  }

  private async refreshCredentials(): Promise<AwsCredentials> {
    const token = await this.getOidcToken();
    if (!token) {
      throw new Error('No OIDC token available for Cognito identity');
    }

    const logins = {
      [environment.cognito.oidcProvider]: token
    };

    const identityResponse = await this.cognitoClient.send(
      new GetIdCommand({
        IdentityPoolId: environment.cognito.identityPoolId,
        Logins: logins
      })
    );

    const credentialsResponse = await this.cognitoClient.send(
      new GetCredentialsForIdentityCommand({
        IdentityId: identityResponse.IdentityId!,
        Logins: logins
      })
    );

    const creds = credentialsResponse.Credentials!;
    this.credentials = {
      accessKeyId: creds.AccessKeyId!,
      secretAccessKey: creds.SecretKey!,
      sessionToken: creds.SessionToken!,
      expiration: creds.Expiration!
    };

    return this.credentials;
  }

  private getOidcToken(): Promise<string | null> {
    return firstValueFrom(
      this.oidcSecurityService.getIdToken().pipe(
        filter(token => !!token),
        take(1),
        timeout(10_000),
        catchError(() => of(null))
      )
    );
  }

  clear(): void {
    this.credentials = null;
    this.refreshPromise = null;
  }
}
